import { AppCard, AppPage } from '../../components/general';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { Fees } from './Fees';
import { useFeeConfigurations, useHudLines, useFeeVersions } from '../../hooks/useFees';
import { FeeConfigurationViewModel, FeeHudLineViewModel, FeeRulesVersionViewModel } from '../../models/fees';
import { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { useSearchParams } from 'react-router-dom';

export const FeesContainer = () => {
  const { user, isLoading: authIsLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const toastRef = useRef<Toast>(null);

  useEffect(() => {
    const versionIdParam = searchParams.get('versionId');
    if (versionIdParam) {
      const versionId = parseInt(versionIdParam, 10);
      if (!isNaN(versionId)) {
        setSelectedVersionId(versionId);
      }
    }
  }, [searchParams]);
  
  const { versionsQuery, publishVersion } = useFeeVersions();
  const { feeConfigurationsQuery, deleteFeeConfiguration, importFeeConfigurations } = useFeeConfigurations();
  const { hudLinesQuery } = useHudLines(selectedVersionId);

  const isLoading = authIsLoading || !user || feeConfigurationsQuery.isLoading || hudLinesQuery.isLoading || versionsQuery.isLoading;

  const handlePublish = async (description: string) => {
    try {
      const publishedVersion = await publishVersion({ description });
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Version ${publishedVersion.versionNumber} published successfully`,
        life: 3000,
      });

      setSelectedVersionId(publishedVersion.feeRulesVersionId);
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to publish version',
        life: 5000,
      });
      throw error;
    }
  };

  const handleImport = async (file: File) => {
    try {
      const result = await importFeeConfigurations(file);
      
      if (result.successfulRows > 0) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Import Completed',
          detail: `Successfully imported ${result.successfulRows} of ${result.totalRows} rows`,
          life: 3000,
        });
      }
      
      return result;
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Import Failed',
        detail: error instanceof Error ? error.message : 'Failed to import fee configurations',
        life: 5000,
      });
      throw error;
    }
  };

  if (isLoading) {
    return (
      <AppPage title="Fees" breadcrumbs="Admin - Fees">
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading Fees...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <>
      <Toast ref={toastRef} />
      <Fees
        feeConfigurations={(feeConfigurationsQuery.data ?? []) as FeeConfigurationViewModel[]}
        hudLines={(hudLinesQuery.data ?? []) as FeeHudLineViewModel[]}
        versions={(versionsQuery.data ?? []) as FeeRulesVersionViewModel[]}
        selectedVersionId={selectedVersionId}
        onVersionChange={setSelectedVersionId}
        onPublish={handlePublish}
        onImport={handleImport}
        onDelete={(id) => {
          if (id == null) return;
          deleteFeeConfiguration(id);
        }}
      />
    </>
  );
};

